import React, { useEffect, useState, useRef } from 'react';
import PropTypes from 'prop-types';

import {
  Accordion,
  AccordionItem,
  AccordionItemHeading,
  AccordionItemButton,
  AccordionItemPanel
} from 'react-accessible-accordion';

import useStore from '../hooks/useStore';

import Text from '../../../text/Text.jsx';
import Tag from '../../../tag/Tag.jsx';
import Button from '../../../button/Button.jsx';
import RegionFilter from './RegionFilter.jsx';

const renderFilters = (
  filters,
  settings,
  tempFilters,
  setTempFilters,
  updateFilters,
  setShowFilters
) => {
  const [regionFilter, setRegionFilter] = useState({
    searchValue: '',
    radiusValue: ''
  });

  return (
    <Accordion className="teaser-focus-filter__groups" allowZeroExpanded>
      {filters.map((filterGroup, i) => (
        <AccordionItem className="teaser-focus-filter__group-item">
          <AccordionItemHeading className="teaser-focus-filter__group-heading">
            <AccordionItemButton className="teaser-focus-filter__group-btn">
              <Text variant="button">{filterGroup.label}</Text>
            </AccordionItemButton>
          </AccordionItemHeading>
          <AccordionItemPanel className="teaser-focus-filter__group-panel">
            {filterGroup.handle === 'locations' ? (
              <>
                <RegionFilter
                  formAction={filterGroup.filter.formAction}
                  geoLocationLabel={filterGroup.filter.geoLocationLabel}
                  handleSearchValue={setRegionFilter}
                  searchLabel={filterGroup.searchLabel}
                  searchValue={regionFilter.searchValue}
                  radiusLabel={filterGroup.filter.radiusLabel}
                  radiusDefault={filterGroup.filter.radiusDefault}
                  radiusOptions={filterGroup.filter.radiusOptions}
                  className="teaser-focus-filter__regions-filter"
                />
                {tempFilters.map(
                  (tempFilter) =>
                    tempFilter.parent === 'locations' && (
                      <Tag
                        // eslint-disable-next-line react/jsx-props-no-spreading
                        {...tempFilter}
                        icon="watchlist-minus"
                        variant={
                          tempFilters.length > 0 &&
                          tempFilters.some((f) => f.slug === tempFilter.slug)
                            ? ''
                            : 'border'
                        }
                        onClick={() =>
                          setTempFilters((state) =>
                            state.includes(tempFilter)
                              ? state.filter((f) => f.slug !== tempFilter.slug)
                              : [...state, tempFilter]
                          )
                        }
                      />
                    )
                )}
              </>
            ) : (
              <>
                {filterGroup.children.map((groupFilter) => (
                  <Tag
                    // eslint-disable-next-line react/jsx-props-no-spreading
                    {...groupFilter}
                    variant={
                      tempFilters.length > 0 &&
                      tempFilters.some((f) => f.slug === groupFilter.slug)
                        ? ''
                        : 'border'
                    }
                    onClick={() =>
                      setTempFilters((state) =>
                        state.some((e) => e.slug === groupFilter.slug)
                          ? state.filter((f) => f.slug !== groupFilter.slug)
                          : [...state, groupFilter]
                      )
                    }
                  />
                ))}
              </>
            )}
            <Button
              labelVariant="copy-label"
              modifiers={['tertiary']}
              label={settings.reset}
              className="teaser-focus-filter__reset"
              onClick={() =>
                setTempFilters((state) =>
                  state.filter((f) => f.parent !== filterGroup.handle)
                )
              }
            />

            <div className="teaser-focus-filter__apply">
              <Button
                labelVariant="button"
                modifiers={['primary']}
                label={settings.apply}
                onClick={() => {
                  document
                    .querySelectorAll('.teaser-focus-filter__group-btn')
                    [i].click();
                  setShowFilters(false);
                  if (filterGroup.handle !== 'locations') {
                    updateFilters(tempFilters);
                  } else {
                    const locationFilterValue =
                      // eslint-disable-next-line no-nested-ternary
                      regionFilter.searchValue && regionFilter.radiusValue
                        ? `${regionFilter.searchValue}:${regionFilter.radiusValue}`
                        : regionFilter.searchValue
                        ? regionFilter.searchValue
                        : null;

                    if (locationFilterValue) {
                      const locationFilter = {
                        label: locationFilterValue,
                        slug: locationFilterValue,
                        parent: 'locations'
                      };

                      setTempFilters((state) =>
                        state.some((e) => e.slug === locationFilter.slug)
                          ? state.filter((f) => f.slug !== locationFilter.slug)
                          : [...state, locationFilter]
                      );

                      updateFilters(
                        tempFilters.some((e) => e.slug === locationFilter.slug)
                          ? tempFilters.filter(
                              (f) => f.slug !== locationFilter.slug
                            )
                          : [...tempFilters, locationFilter]
                      );
                    } else {
                      updateFilters(tempFilters);
                    }
                  }
                }}
              />
            </div>
          </AccordionItemPanel>
        </AccordionItem>
      ))}
    </Accordion>
  );
};

const FocusFilter = ({ data }) => {
  const { settings, filters, emptyState } = data;
  const [showFilters, setShowFilters] = useState(false);
  const [tempFilters, setTempFilters] = useState([]);
  const filtersRef = useRef(null);

  const updateFilters = useStore((state) => state.updateFilters);
  const activeFilters = useStore((state) => state.filters);

  useEffect(() => {
    const handleClickOutside = (event) => {
      if (filtersRef.current && !filtersRef.current.contains(event.target)) {
        setShowFilters(false);
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, [filtersRef]);

  useEffect(() => {
    const urlSearchParams = new URLSearchParams(window.location.search);
    const params = Object.fromEntries(urlSearchParams.entries());

    if (params?.f) {
      const decodedFilters = JSON.parse(window.atob(params.f));
      setTempFilters(decodedFilters);
      updateFilters(decodedFilters);
    }
  }, []);

  useEffect(() => {
    const url = new URL(window.location);
    if (activeFilters.length === 0) {
      url.searchParams.delete('f');
    } else {
      const encodedActiveFilters = window.btoa(JSON.stringify(activeFilters));
      url.searchParams.set('f', encodedActiveFilters);
    }

    window.history.pushState(null, '', url.toString());
  }, [activeFilters]);

  return (
    <>
      <div className="teaser-focus-filter__container">
        <button
          type="button"
          aria-controls="focusFilter"
          aria-expanded={showFilters}
          onClick={() => setShowFilters(!showFilters)}
          className="teaser-focus-filter__toggle"
        >
          <Text variant="button">{settings.toggle}</Text>
        </button>
        <div
          ref={filtersRef}
          id="focusFilter"
          className="teaser-focus-filter__filters"
        >
          {renderFilters(
            filters,
            settings,
            tempFilters,
            setTempFilters,
            updateFilters,
            setShowFilters
          )}
        </div>
      </div>

      <div className="teaser-focus-filter__output">
        {activeFilters.length > 0 ? (
          <div className="teaser-focus-filter__output-filters">
            <Text>{settings.focusLabel}</Text>
            <div className="teaser-focus-filter__output-active-filters">
              {activeFilters.map((activeFilter) => (
                <Tag
                  // eslint-disable-next-line react/jsx-props-no-spreading
                  {...activeFilter}
                  icon="watchlist-minus"
                  onClick={() => {
                    setTempFilters((state) =>
                      state.filter((f) => f.slug !== activeFilter.slug)
                    );
                    updateFilters(
                      activeFilters.filter((f) => f.slug !== activeFilter.slug)
                    );
                  }}
                />
              ))}
              <Button
                labelVariant="copy-label"
                modifiers={['tertiary']}
                label={settings.reset}
                className="teaser-focus-filter__reset"
                onClick={() => {
                  setTempFilters([]);
                  updateFilters([]);
                }}
              />
            </div>
          </div>
        ) : (
          <div className="teaser-focus-filter__output-empty">
            <Text variant="headline-3-sans">{emptyState.headline}</Text>
            <Text>{emptyState.copy}</Text>
          </div>
        )}
      </div>
    </>
  );
};

FocusFilter.propTypes = {
  data: PropTypes.shape({
    settings: PropTypes.shape({
      toggle: PropTypes.string,
      reset: PropTypes.string,
      apply: PropTypes.string,
      focusLabel: PropTypes.string
    }),
    filters: PropTypes.arrayOf(
      PropTypes.shape({
        id: PropTypes.string,
        label: PropTypes.string,
        children: PropTypes.arrayOf(PropTypes.shape())
      })
    ),
    emptyState: PropTypes.shape({
      headline: PropTypes.string,
      copy: PropTypes.string
    })
  }).isRequired
};

export default FocusFilter;
